import { useEffect, useState } from "react";
import { X } from "lucide-react";

const DISMISS_KEY = "ledger_install_dismissed";
const DISMISS_TTL_DAYS = 30;

const inStandaloneMode = () => {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari-specific flag
      window.navigator.standalone === true
    );
  } catch (e) {
    return false;
  }
};

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports itself as Mac — sniff for touch to catch it.
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (/^Mac/.test(navigator.platform) && (navigator.maxTouchPoints || 0) > 1);
  if (isIOS) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
};

const readDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts;
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_TTL_DAYS;
  } catch (e) {
    return false;
  }
};

const writeDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now() }));
  } catch (e) {
    /* ignore */
  }
};

export default function InstallPrompt() {
  const [platform, setPlatform] = useState("other");
  const [dismissed, setDismissed] = useState(true); // default hidden until checks run
  const [standalone, setStandalone] = useState(true);
  const [deferredEvent, setDeferredEvent] = useState(null);

  useEffect(() => {
    setPlatform(detectPlatform());
    setStandalone(inStandaloneMode());
    setDismissed(readDismissed());

    const onBeforeInstall = (e) => {
      // Prevent Chrome's mini-infobar — we'll surface our own button.
      e.preventDefault();
      setDeferredEvent(e);
    };
    const onInstalled = () => {
      setStandalone(true);
      setDeferredEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const onDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  const onAndroidInstall = async () => {
    if (!deferredEvent) return;
    try {
      deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      setDeferredEvent(null);
      if (choice?.outcome === "dismissed") onDismiss();
    } catch (e) {
      /* ignore */
    }
  };

  // Visibility gates: mobile only, not already standalone, not dismissed.
  if (standalone) return null;
  if (dismissed) return null;
  if (platform === "other") return null;
  if (platform === "android" && !deferredEvent) return null; // wait for event

  const testid =
    platform === "ios" ? "install-prompt-ios" : "install-prompt-android";

  return (
    <div
      data-testid={testid}
      style={{
        marginTop: 18,
        padding: "12px 14px",
        borderRadius: 14,
        backgroundColor: "#1a1917",
        border: "1px solid #2a2721",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontFamily:
            platform === "ios"
              ? "'Inter', system-ui, sans-serif"
              : "'Inter', system-ui, sans-serif",
        }}
      >
        {platform === "ios" ? (
          <span
            data-testid="install-prompt-ios-text"
            style={{
              fontSize: 12.5,
              color: "#d6d2c3",
              lineHeight: 1.45,
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            <span aria-hidden style={{ marginRight: 6 }}>📱</span>
            Tap <strong style={{ color: "#f4f1ea" }}>Share</strong> →
            <strong style={{ color: "#e8dc2d" }}> Add to Home Screen</strong> for
            one-tap access
          </span>
        ) : (
          <button
            data-testid="install-prompt-install-btn"
            onClick={onAndroidInstall}
            style={{
              padding: "10px 16px",
              borderRadius: 999,
              border: "none",
              backgroundColor: "#e8dc2d",
              color: "#0f0e0c",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Install HandInvoice
          </button>
        )}
      </div>
      <button
        data-testid="install-prompt-dismiss"
        aria-label="Dismiss"
        onClick={onDismiss}
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          border: "none",
          backgroundColor: "transparent",
          color: "#6b6558",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          flex: "0 0 auto",
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
